import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Table as TableIcon, AlertTriangle, CheckCircle2, ShieldAlert, BadgeAlert, Clock4, CalendarClock, Landmark } from 'lucide-react';
import { formatMinutes } from '../../../components/reports';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { GenerateUsersMonthlyPayslips, MarkMonthlyPayslipsAsUnpaid, GetUsersMonthlyPayslips, MarkMonthlyPayslipsAsPaid, GetPayrollPreflight } from '../../../api/Payroll/monthlyPayroll';
import { MonthlyPayrollInput, MonthlyPayrollView, PayrollPreflightView } from '../../../models/payroll';
import { CompanyCurrencyRate, Country, Currency, Department, UserView } from '../../../models/general';
import { getCompanyRules, getDepartments } from '../../../api/General/Company';
import { getUsersCountries } from '../../../api/General/Country';
import { GetCompanyRates } from '../../../api/General/currency';
import { formatNumber } from '../../../utils';
import { appMonthName } from '../../../utils/appDateFormat';
import CurrencyDropdown from '../../../components/currencyDropdown';
import EmployeesList from '../../../components/employeesList';
import Pagination from '../../../components/Pagination';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import StatusBadge from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { useCompanyClock } from '../../../hooks/useCompanyClock';
import { companyNowYearMonth } from '../../../utils/companyClock';
import { PageLayout } from '../../../components/ui/PageLayout';

const TablePayroll: React.FC = () => {
  const { t } = useTranslation();
  const userId = useSelector((s: any) => s.user.userId);
  const company = useSelector((s: any) => s.user.company);
  const companyCurrencyId = useSelector((s: any) => s.user.companyCurrencyId);
  const CompanyCurrencyRate = useSelector((s: any) => s.user.CompanyCurrencyRate);

  const { toast, showToast, dismissToast } = useToast();

  const PAGE_SIZE = 10;
  const [loading, setLoading] = useState(false);
  const [payslips, setPayslips] = useState<MonthlyPayrollView[]>([]);
  const [filtered, setFiltered] = useState<MonthlyPayrollView[]>([]);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [yearMonth, setYearMonth] = useState(new Date());
  const [departments, setDepartments] = useState<Department[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [companyCurrencyRates, setCompanyCurrencyRates] = useState<CompanyCurrencyRate[]>([]);
  const [, setBaseCurrency] = useState<Currency | null>(null);
  const [currency, setCurrency] = useState<any>(0);
  // Filters
  const [status, setStatus] = useState('All');
  const [employeeName, setEmployeeName] = useState('');
  const [departmentIds, setDepartmentIds] = useState<number[]>([]);
  const [countryId, setCountryId] = useState<number>(0);
  const [position, setPosition] = useState('');
  const [minSalary, setMinSalary] = useState<number | ''>('');
  const [maxSalary, setMaxSalary] = useState<number | ''>('');
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Dialog — three-step flow ported from rocketnew /payroll-run-wizard:
  //   'select'   → pick employees
  //   'preflight'→ surface missing IBAN / labour card / pending approvals / already-paid
  //   'confirm'  → final go/no-go
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserView[]>([]);
  const [step, setStep] = useState<'select' | 'preflight' | 'confirm'>('select');
  const [preflight, setPreflight] = useState<PayrollPreflightView | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  // CO-341 — the server's refusal text for the last failed generate, rendered inline on the confirm
  // step. See generateMonthlyPayroll for why a toast alone was not enough.
  const [generateError, setGenerateError] = useState<string | null>(null);

  const year = yearMonth.getFullYear();
  const month = yearMonth.getMonth() + 1;
  const monthName = appMonthName(new Date(year, month - 1));

  // 🔴 CO-341 (client side of the future-period guard) — the latest period the GENERATE flow may target.
  //
  // The server refuses a future period by comparing against the COMPANY's local clock
  // (MonthlyPayrollService: `currentYear > companyNow.Year || (== && currentMonth > companyNow.Month)`).
  //
  // 2026-09-17: the company's zone IS now available to this screen — it rides on `state.user.company
  // .timeZone` (UserCompanyView), IANA-normalised server-side — so this reads the company's ACTUAL
  // current month instead of approximating it. The approximation it replaces clamped to `utcNow - 12h`
  // (the westernmost IANA offset), which was a correct *subset* of the server's answer but withheld a
  // brand-new month from every company east of UTC for up to 12 hours. Neither error can happen now.
  //
  // Degraded case: `useCompanyClock` falls back to UTC — the same clock the server falls back to for a
  // company with no zone — and never to the browser's. The rung taken is in `companyClock.source`; see
  // `utils/companyClock.ts` for the chain. (A company with no zone at all cannot generate payroll
  // anyway: MonthlyPayrollService refuses it with "Company is not properly configured".)
  //
  // ⚠️ Only the GENERATE path is clamped. The picker below is SHARED with the view filter (it drives
  // loadPayslips, the preflight's already-paid list and `existingPayslipUserIds`), and viewing a future
  // month is legitimate, so the picker's own range is left untouched.
  const companyClock = useCompanyClock();
  const latestGeneratable = companyNowYearMonth(companyClock);
  const isFutureGeneratePeriod =
    year > latestGeneratable.year || (year === latestGeneratable.year && month > latestGeneratable.month);

  // One-shot lookups: departments, countries, company currency rates, base currency.
  // These don't depend on the selected month/currency, so they load once per company and
  // don't re-fire when the user changes year/month or the currency dropdown.
  useEffect(() => {
    if (!company?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [departmentsResp, countriesResp, ratesResp, rules] = await Promise.all([
          getDepartments(company.id),
          getUsersCountries(userId),
          GetCompanyRates(),
          getCompanyRules(company.id),
        ]);
        if (cancelled) return;
        setDepartments(departmentsResp.payload || []);
        setCountries(countriesResp.payload || []);
        const baseCurr: CompanyCurrencyRate = {
          id: 0,
          baseCurrency: rules.payload.currency,
          baseCurrencyId: rules.payload.currency.id,
          foreignCurrency: rules.payload.currency,
          foreignCurrencyId: 0,
          company: company,
          companyId: company.id,
          rate: 0,
        } as CompanyCurrencyRate;
        setBaseCurrency(rules.payload.currency);
        setCompanyCurrencyRates([baseCurr, ...(ratesResp.payload || [])]);
      } catch (e) {
        console.error(e);
        showToast(t('Failed to load payroll data'), 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [company, userId, t, showToast]);

  const loadPayslips = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      // Server scopes by loggedInUser.CompanyId — no need to send the full user-id list from the client.
      const payrollData = await GetUsersMonthlyPayslips({
        year, month,
        currencyId: currency?.foreignCurrencyId || companyCurrencyId,
      });
      setPayslips(payrollData.payload || []);
      setFiltered(payrollData.payload || []);
    } catch (e) {
      console.error(e);
      showToast(t('Failed to load payroll data'), 'error');
    } finally {
      setLoading(false);
    }
  }, [company?.id, year, month, currency?.foreignCurrencyId, companyCurrencyId, t, showToast]);

  useEffect(() => { loadPayslips(); }, [loadPayslips]);

  const toggleRow = (id: number, userId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const allChecked = filtered.length > 0 && selectedIds.size === filtered.length;
  const indeterminate = selectedIds.size > 0 && selectedIds.size < filtered.length;
  const toggleAll = () => {
    if (allChecked) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(f => f.id)));
  };

  const handleYearMonthChange = (date: Date) => {
    setYearMonth(date);
  };

  const getYearMonthPickerFromMonth = () => {
    const d = new Date(); d.setMonth(0); d.setFullYear(new Date().getFullYear() - 8); return d;
  };

  const getYearMonthPickerToMonth = () => {
    const d = new Date(); d.setMonth(12); d.setFullYear(new Date().getFullYear() + 1); return d;
  };

  const markPaid = async () => {
    try {
      if (!selectedIds.size) return;
      await MarkMonthlyPayslipsAsPaid({ year, month, payslipIds: Array.from(selectedIds) } as MonthlyPayrollInput);
      showToast(t('Records marked as paid'), 'success');
      await loadPayslips();
    } catch {
      showToast(t('Action failed'), 'error');
    }
  };

  const markUnpaid = async () => {
    try {
      if (!selectedIds.size) return;
      await MarkMonthlyPayslipsAsUnpaid({ year, month, payslipIds: Array.from(selectedIds) } as MonthlyPayrollInput);
      showToast(t('Records marked as unpaid'), 'success');
      await loadPayslips();
    } catch { showToast(t('Action failed'), 'error'); }
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleOpenMonthlyPay = () => {
    // CO-341 — belt and braces: the trigger below is disabled for a future period, but this is the one
    // entry point into the generate flow, so the guard lives here too rather than only in the markup.
    if (isFutureGeneratePeriod) {
      showToast(t('Payroll cannot be generated for a future period. Select the current month or an earlier one.'), 'error');
      return;
    }
    setStep('select'); setPreflight(null); setGenerateError(null); setOpenDialog(true);
  };
  const handleCloseMonthlyPay = () => { setOpenDialog(false); setStep('select'); setPreflight(null); setSelectedUsers([]); setGenerateError(null); };

  const runPreflight = async () => {
    if (!selectedUsers.length) return;
    // CO-341 — same clamp as the entry point; the picker can move while the wizard is open.
    if (isFutureGeneratePeriod) {
      showToast(t('Payroll cannot be generated for a future period. Select the current month or an earlier one.'), 'error');
      return;
    }
    setPreflightLoading(true);
    try {
      const resp = await GetPayrollPreflight({ year, month, userIds: selectedUsers.map(u => u.id) });
      setPreflight(resp.payload || null);
      setStep('preflight');
    } catch (e) {
      console.error('Preflight error:', e);
      showToast(t('Failed to run pre-flight check'), 'error');
    } finally {
      setPreflightLoading(false);
    }
  };

  const generateMonthlyPayroll = async (users: UserView[]) => {
    setGenerateError(null);
    try {
      setLoading(true);
      // CO-341: the SAME year/month the preflight above ran against (GetPayrollPreflight({ year, month, … })).
      // Passing them here is what stops the wizard's month picker and the write from disagreeing.
      await GenerateUsersMonthlyPayslips(users.map(u => u.id), year, month);
      showToast(t('Payroll generated'), 'success');
      await loadPayslips();
      handleCloseMonthlyPay();
    } catch (e: any) {
      console.error('Generate monthly payroll error:', e);
      // CO-341 — server refusals carry a COMPLETE human-readable sentence in `errors[0].description`
      // (the future-period 400 names the latest generatable month outright). Surface that text as the
      // message instead of appending it to a generic "Generation failed", and — the part that actually
      // mattered — keep the dialog OPEN and render it inline. The old code closed the dialog in
      // `finally`, so the refusal reason vanished underneath an auto-dismissing toast and HR was left
      // with a wizard that had simply shut with nothing generated. Toast stays as the at-a-glance
      // signal; the inline block is the one that persists until they act on it.
      //
      // ⚠️ `.description` not `.message` — that is the shape of this app's `Error` model.
      const description: string | undefined = e?.errors?.[0]?.description;
      setGenerateError(description || t('Generation failed'));
      showToast(description || t('Generation failed'), 'error');
    }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const lowerName = employeeName.trim().toLowerCase();
    const lowerPos = position.trim().toLowerCase();
    const next = payslips.filter(p => {
      try {
        const fullName = (p.user.fullName || '').toString().toLowerCase();
        const title = (p.user.title || '').toString().toLowerCase();
        const statusMatch = status === 'All' || (status === 'Paid' && p.status) || (status === 'Unpaid' && !p.status);
        const nameMatch = !lowerName || fullName.includes(lowerName);
        const deptMatch = !departmentIds.length || departmentIds.includes(p.user.departmentId || 0);
        const countryMatch = countryId === 0 || p.user.countryId === countryId;
        const posMatch = !lowerPos || title.includes(lowerPos);
        const totalNet = Number(p.totalNetPay ?? 0);
        const minOk = !minSalary || totalNet >= +minSalary;
        const maxOk = !maxSalary || totalNet <= +maxSalary;
        return statusMatch && nameMatch && deptMatch && countryMatch && posMatch && minOk && maxOk;
      } catch (err) {
        console.error('Filter error for payslip row', p, err);
        return false;
      }
    });
    setFiltered(next); setPage(1); setSelectedIds(new Set());
  }, [status, employeeName, departmentIds, countryId, position, minSalary, maxSalary, payslips]);

  const resetFilters = () => {
    setStatus('All'); setEmployeeName(''); setDepartmentIds([]); setCountryId(0); setPosition(''); setMinSalary(''); setMaxSalary('');
  };

  const summary = useMemo(() => {
    return {
      total: filtered.reduce((a, c) => a + c.totalNetPay, 0),
      paid: filtered.filter(f => f.status).length,
      unpaid: filtered.filter(f => !f.status).length
    };
  }, [filtered]);

  const existingPayslipUserIds = useMemo(
    () => new Set(payslips.map(p => p.userId)),
    [payslips]
  );

  // Hourly pay §5.4 — read once into locals so the JSX below narrows on plain consts.
  const hourlyPendingRows = preflight?.hourlyPendingShifts;
  const hourlyPendingCount = preflight?.hourlyPendingShiftsCount ?? hourlyPendingRows?.length ?? 0;
  const hourlyPendingMinutes = preflight?.hourlyPendingMinutes ?? 0;
  // Hourly pay §5.3 carry-forward (D10) — negative Hourly corrections this run holds back so no payslip
  // goes below zero. Amounts arrive signed (negative); shown as they will appear on next month's line.
  const hourlyCarryRows = preflight?.hourlyCarriedForward ?? [];
  const hourlyCarryTotal = preflight?.hourlyCarriedForwardTotal
    ?? hourlyCarryRows.reduce((sum, r) => sum + r.amount, 0);
  // Hourly pay §8 — the pension / GOSI base this run uses for each hourly employee (their approved hourly
  // earnings). The server omits the field when the selection has no hourly employee.
  const hourlyStatutoryRows = preflight?.hourlyStatutoryBase ?? [];
  // CO-657 — both default to [] so an older server (which sends neither field) renders nothing extra.
  const postExitSkipped = preflight?.exitedBeforePeriod ?? [];
  const postExitPayslips = preflight?.postExitPayslips ?? [];

  return (
    <PageLayout
      title={t('Payroll Table')}
      icon={TableIcon}
      accentColor="emerald"
      maxWidth="1600px"
      headerActions={
        <div className="flex flex-wrap items-center gap-2" data-testid="bulk-actions">
          <Button variant="outline" size="sm" onClick={markPaid} disabled={!selectedIds.size} data-testid="mark-paid-btn">{t('Mark Records As Paid')}</Button>
          <Button variant="outline" size="sm" onClick={markUnpaid} disabled={!selectedIds.size}>{t('Mark Records As Unpaid')}</Button>
          {/* CO-341 — the ONLY entry point into the generate flow, so this is where the future-period
              clamp bites. The month picker itself stays unclamped because it also drives the view filter. */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenMonthlyPay}
            disabled={isFutureGeneratePeriod}
            title={isFutureGeneratePeriod
              ? t('Payroll cannot be generated for a future period. Select the current month or an earlier one.')
              : undefined}
            iconName="Download"
            data-testid="generate-monthly-payroll-btn"
          >
            {t('Generate Monthly Payroll')}
          </Button>
          <Button asChild variant="outline" size="sm" iconName="ExternalLink">
            <Link to="/payroll/wps-center" data-testid="wps-center-link">{t('WPS / Mudad')}</Link>
          </Button>
        </div>
      }
    >
    <div className="flex flex-col gap-5">
      {/* Toolbar — Filter / Currency / Year-Month picker on the left, summary
          badges on the right. Wraps to multiple lines only when the viewport
          can't fit them on one row. */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="payroll-toolbar">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowFilters(s => !s)}
            iconName={showFilters ? 'Minus' : 'Plus'}
            iconPosition="right"
          >
            {t('Filter')}
          </Button>
          <CurrencyDropdown
            value={currency}
            options={companyCurrencyRates}
            onOptionChange={(option: any) => setCurrency(option)}
          />
          <YearMonthPicker
            date={yearMonth}
            onChange={handleYearMonthChange}
            fromMonth={getYearMonthPickerFromMonth()}
            toMonth={getYearMonthPickerToMonth()}
            isRtl={false}
          />
          {/* CO-341 — a disabled <button>'s native title tooltip is unreliable (several browsers
              suppress pointer events on it), so the reason the Generate action went away is stated
              here in the open, next to the control that caused it. Viewing a future month is still
              perfectly legitimate — this is a note, not an error. */}
          {isFutureGeneratePeriod && (
            <span
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 px-2.5 py-1 text-xs font-medium text-amber-700"
              data-testid="future-period-generate-hint"
            >
              <AlertTriangle size={14} />
              {t('Payroll cannot be generated for a future period. Select the current month or an earlier one.')}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center rounded-full border border-border px-3 py-1 text-xs font-medium text-foreground">
            {t('Total')}: {formatNumber(summary.total)}
          </span>
          <span className="inline-flex items-center rounded-full border border-success/30 bg-success/5 px-3 py-1 text-xs font-medium text-success">
            {t('Paid')}: {summary.paid}
          </span>
          <span className="inline-flex items-center rounded-full border border-destructive/30 bg-destructive/5 px-3 py-1 text-xs font-medium text-destructive">
            {t('Unpaid')}: {summary.unpaid}
          </span>
          <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
            {t('Selected')}: {selectedIds.size}
          </span>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="rounded-lg border border-border p-4 flex flex-wrap gap-4">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-foreground">{t('Status')}</span>
            <div className="flex items-center gap-4">
              {(['All', 'Paid', 'Unpaid'] as const).map(val => (
                <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="payroll-status"
                    value={val}
                    checked={status === val}
                    onChange={() => setStatus(val)}
                    className="h-4 w-4 border-border text-primary focus:ring-primary accent-primary"
                  />
                  {t(val)}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 min-w-[160px]">
            <Input
              data-testid="employee-search"
              label={t('common:Employee')}
              value={employeeName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployeeName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[200px]">
            <Select
              multiple
              searchable
              data-testid="department-filter"
              options={departments.map(d => ({ value: d.id, label: d.name }))}
              value={departmentIds}
              onChange={setDepartmentIds}
              label={t('Department')}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[180px]">
            <Select
              searchable
              options={countries.map(c => ({ value: c.id, label: c.name }))}
              value={countryId || ''}
              onChange={(val: any) => setCountryId(val || 0)}
              label={t('Country')}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[160px]">
            <Input
              label={t('Position')}
              value={position}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPosition(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[140px]">
            <Input
              label={t('Minimum Salary')}
              type="number"
              value={minSalary}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinSalary(e.target.value ? +e.target.value : '')}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[140px]">
            <Input
              label={t('Maximum Salary')}
              type="number"
              value={maxSalary}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxSalary(e.target.value ? +e.target.value : '')}
            />
          </div>
          <div className="flex flex-col justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={resetFilters}>{t('Reset')}</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-border overflow-x-auto" data-testid="payroll-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  indeterminate={indeterminate}
                  checked={allChecked}
                  onChange={toggleAll}
                />
              </TableHead>
              <TableHead>{t('Employee Name')}</TableHead>
              <TableHead className="text-end">{t('Gross Salary')}</TableHead>
              <TableHead className="text-end">{t('Variable Pay')}</TableHead>
              <TableHead className="text-end">{t('Adjustments')}</TableHead>
              <TableHead className="text-end" title={t('payroll.ot.tooltip', 'FDL 33/2021 Art. 19/20/21 — 1.25× weekday, 1.50× rest-day/holiday/night')}>
                {t('payroll.ot.header', 'OT')}
              </TableHead>
              <TableHead className="text-end">{t('Total Net Pay')}</TableHead>
              <TableHead className="text-end">{t('common:Status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <Spinner size="md" />
                </TableCell>
              </TableRow>
            )}
            {!loading && !paged.length && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm">
                  {t('No records')}
                </TableCell>
              </TableRow>
            )}
            {!loading && paged.map(row => {
              const currCode = currency?.foreignCurrency?.currencyCode || CompanyCurrencyRate?.currencyCode;
              const checked = selectedIds.has(row.id);
              return (
                <TableRow key={row.id}>
                  <TableCell className="w-10">
                    <Checkbox checked={checked} onChange={() => toggleRow(row.id, row.userId, !checked)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/profile/${row.userId}/payroll/active-payroll-month`} className="font-semibold text-primary hover:underline">
                        {row.user.fullName}
                      </Link>
                      {row.onProbation && row.probationExceeded && (
                        <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-semibold whitespace-nowrap">
                          {t('payroll.probation.exceeded', 'Probation exceeded')}
                        </span>
                      )}
                      {row.onProbation && !row.probationExceeded && (row.probationDays ?? 0) > 150 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold whitespace-nowrap">
                          {t('payroll.probation.dayLabel', 'Probation — {{days}}d', { days: row.probationDays })}
                        </span>
                      )}
                    </div>
                    <span className="block text-xs text-muted-foreground">{row.user.title}</span>
                  </TableCell>
                  <TableCell className="text-end">{formatNumber(row.grossSalary)} {currCode}</TableCell>
                  <TableCell className="text-end">{formatNumber(row.variablePay)} {currCode}</TableCell>
                  <TableCell className="text-end">{formatNumber(row.adjustments)} {currCode}</TableCell>
                  <TableCell className="text-end">
                    <div className="flex flex-col items-end">
                      <span>{formatNumber(Number(row.overtimePay || 0))} {currCode}</span>
                      {((row.regularOvertimeHours ?? 0) > 0 || (row.premiumOvertimeHours ?? 0) > 0) && (
                        <span className="text-[10px] text-muted-foreground">
                          {formatNumber(row.regularOvertimeHours ?? 0)}h · {formatNumber(row.premiumOvertimeHours ?? 0)}h*
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-end">{formatNumber(row.totalNetPay)} {currCode}</TableCell>
                  <TableCell className="text-end">
                    <StatusBadge variant={row.status ? 'success' : 'error'} size="sm">
                      {row.status ? t('PAID') : t('UNPAID')}
                    </StatusBadge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-end p-3">
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Generate Monthly Payroll Dialog — three-step flow (select → preflight → confirm) */}
      <Dialog open={openDialog} onOpenChange={(isOpen) => { if (!isOpen) handleCloseMonthlyPay(); }}>
          <DialogContent size="xl" className="flex flex-col max-w-3xl max-h-[85vh] overflow-y-hidden" data-testid="generate-payroll-dialog">
              <DialogHeader>
                  <DialogTitle>
                      {step === 'select' && t('Select Employees to Generate Payroll')}
                      {step === 'preflight' && t('Pre-Payroll Validation')}
                      {step === 'confirm' && t('Confirm Payroll Generation')}
                  </DialogTitle>
                  <DialogDescription>
                      <span className="inline-flex items-center gap-2">
                          <span>{monthName} {year}</span>
                          <span className="text-xs text-muted-foreground">
                              · {t('Step {{n}} of 3', { n: step === 'select' ? 1 : step === 'preflight' ? 2 : 3 })}
                          </span>
                      </span>
                  </DialogDescription>
              </DialogHeader>

              {step === 'select' && (
                  <>
                      <DialogBody className="flex-1 overflow-y-auto">
                          <EmployeesList
                              key={openDialog ? 'open' : 'closed'}
                              onSelectionChange={(users: UserView[]) => setSelectedUsers(users)}
                              existingPayslipUserIds={existingPayslipUserIds}
                          />
                      </DialogBody>
                      <DialogFooter>
                          <Button variant="outline" onClick={handleCloseMonthlyPay} data-testid="generate-cancel-btn">
                              {t('Cancel')}
                          </Button>
                          {/* CO-341 — the toolbar picker stays live while this dialog is open, so the
                              period can turn future mid-wizard. Block the step that commits to it. */}
                          <Button
                              disabled={!selectedUsers.length || loading || preflightLoading || isFutureGeneratePeriod}
                              loading={preflightLoading}
                              onClick={runPreflight}
                              data-testid="generate-continue-btn"
                          >
                              {t('Continue')} ({selectedUsers.length})
                          </Button>
                      </DialogFooter>
                  </>
              )}

              {step === 'preflight' && (
                  <>
                      <DialogBody className="flex-1 overflow-y-auto space-y-3" data-testid="preflight-panel">
                          <PreflightSection
                              icon={ShieldAlert}
                              tone={preflight && preflight.missingIbanCount > 0 ? 'error' : 'ok'}
                              title={t('Bank Details (IBAN)')}
                              count={preflight?.missingIbanCount ?? 0}
                              names={preflight?.missingIban.map(u => u.fullName) ?? []}
                              testId="preflight-iban"
                          />
                          <PreflightSection
                              icon={BadgeAlert}
                              tone={preflight && preflight.missingLabourCardCount > 0 ? 'error' : 'ok'}
                              title={t('Labour Card Number')}
                              count={preflight?.missingLabourCardCount ?? 0}
                              names={preflight?.missingLabourCard.map(u => u.fullName) ?? []}
                              testId="preflight-labour-card"
                          />
                          <PreflightSection
                              icon={AlertTriangle}
                              tone={preflight && preflight.alreadyPaidCount > 0 ? 'error' : 'ok'}
                              title={t('Already Paid for {{month}} {{year}}', { month: monthName, year })}
                              count={preflight?.alreadyPaidCount ?? 0}
                              names={preflight?.alreadyPaid.map(u => u.fullName) ?? []}
                              testId="preflight-already-paid"
                          />
                          <PreflightSection
                              icon={AlertTriangle}
                              tone={preflight && preflight.pendingApprovalsCount > 0 ? 'warn' : 'ok'}
                              title={t('Items Awaiting Approval')}
                              count={preflight?.pendingApprovalsCount ?? 0}
                              names={preflight?.pendingApprovals.map(a => `${a.fullName} (${a.pendingClaims + a.pendingAdjustments + a.pendingVariablePays})`) ?? []}
                              footnote={t('Pending claims, adjustments, or variable pays will not be included until approved.')}
                              testId="preflight-pending-approvals"
                          />
                          {/* Hourly pay §5.4 (D6) — hourly shifts still Pending for this month. A WARNING,
                              never a blocker (it does not feed `hasBlockers`): they are paid in the next
                              run once approved. Rendered only when the server sends the field. */}
                          {hourlyPendingRows !== undefined && (
                              <PreflightSection
                                  icon={Clock4}
                                  tone={hourlyPendingCount > 0 ? 'warn' : 'ok'}
                                  title={t('payrollPreflight.hourlyPendingTitle')}
                                  count={hourlyPendingCount}
                                  names={hourlyPendingRows.map(h => t('payrollPreflight.hourlyPendingName', {
                                      name: h.fullName,
                                      n: h.pendingCount,
                                      hours: formatMinutes(h.pendingMinutes),
                                  }))}
                                  footnote={
                                      hourlyPendingMinutes > 0
                                          ? t('payrollPreflight.hourlyPendingFootnoteTotal', { hours: formatMinutes(hourlyPendingMinutes) })
                                          : t('payrollPreflight.hourlyPendingFootnote')
                                  }
                                  testId="preflight-hourly-pending"
                              />
                          )}
                          {/* Hourly pay §5.3 (D10) — information, not a warning and never a blocker: the run
                              is correct, it just stops each payslip at zero and deducts the rest next month.
                              Only rendered when something is actually held back. */}
                          {hourlyCarryRows.length > 0 && (
                              <PreflightSection
                                  icon={CalendarClock}
                                  tone="info"
                                  title={t('payrollPreflight.hourlyCarriedTitle')}
                                  count={hourlyCarryRows.length}
                                  names={hourlyCarryRows.map(r => t('payrollPreflight.hourlyCarriedName', {
                                      name: r.fullName,
                                      amount: formatNumber(r.amount),
                                  }))}
                                  footnote={
                                      hourlyCarryRows.length > 1
                                          ? t('payrollPreflight.hourlyCarriedFootnoteTotal', { amount: formatNumber(hourlyCarryTotal) })
                                          : t('payrollPreflight.hourlyCarriedFootnote')
                                  }
                                  testId="preflight-hourly-carried-forward"
                              />
                          )}
                          {/* CO-657 (exit-date lifecycle §4.2) — the two exit-date facts, kept apart
                              because they need different things from HR.

                              SKIPPED is informational: the run will not write a payslip for someone who
                              had already left when the period began, and saying so up front is what
                              stops a shorter-than-expected run looking like a bug.

                              FLAGGED is the one that matters. A payslip already on file for a month
                              AFTER the exit is money that may have been paid for work nobody did. It is
                              never deleted — deleting a payslip would orphan every variable-pay line it
                              consumed — so the flag plus this list is the whole mechanism. Warning tone,
                              not blocker: HR may legitimately be regenerating a closed month. */}
                          {postExitSkipped.length > 0 && (
                              <PreflightSection
                                  icon={CalendarClock}
                                  tone="info"
                                  title={t('payrollPreflight.exitedBeforePeriodTitle')}
                                  count={postExitSkipped.length}
                                  names={postExitSkipped.map(u => u.fullName)}
                                  footnote={t('payrollPreflight.exitedBeforePeriodFootnote')}
                                  testId="preflight-exited-before-period"
                              />
                          )}
                          {postExitPayslips.length > 0 && (
                              <PreflightSection
                                  icon={AlertTriangle}
                                  tone="warn"
                                  title={t('payrollPreflight.postExitPayslipsTitle')}
                                  count={postExitPayslips.length}
                                  names={postExitPayslips.map(p => t('payrollPreflight.postExitPayslipName', {
                                      name: p.fullName,
                                      period: p.period,
                                  }))}
                                  footnote={t('payrollPreflight.postExitPayslipsFootnote')}
                                  testId="preflight-post-exit-payslips"
                              />
                          )}
                          {/* Hourly pay §8 — information only: an hourly employee has no salary component, so
                              HR needs to see what pension / GOSI is computed on before generating. */}
                          {hourlyStatutoryRows.length > 0 && (
                              <PreflightSection
                                  icon={Landmark}
                                  tone="info"
                                  title={t('payrollPreflight.hourlyStatutoryTitle')}
                                  count={hourlyStatutoryRows.length}
                                  names={hourlyStatutoryRows.map(r => t('payrollPreflight.hourlyStatutoryName', {
                                      name: r.fullName,
                                      base: formatNumber(r.basicWage),
                                      deduction: formatNumber(r.employeeDeduction),
                                  }))}
                                  footnote={t('payrollPreflight.hourlyStatutoryFootnote')}
                                  testId="preflight-hourly-statutory-base"
                              />
                          )}
                      </DialogBody>
                      <DialogFooter>
                          <Button variant="outline" onClick={() => setStep('select')}>
                              {t('Back')}
                          </Button>
                          <Button
                              disabled={!preflight || preflight.hasBlockers || loading}
                              onClick={() => setStep('confirm')}
                              data-testid="preflight-continue-btn"
                          >
                              {preflight?.hasBlockers ? t('Resolve Blockers to Continue') : t('Continue')}
                          </Button>
                      </DialogFooter>
                  </>
              )}

              {step === 'confirm' && (
                  <>
                      <DialogBody className="space-y-4">
                          <p className="text-sm text-foreground">
                              {t('Generate payroll for {{count}} employees?', { count: selectedUsers.length })}
                          </p>
                          <p className="text-sm text-muted-foreground">
                              {monthName} {year}
                          </p>
                          {selectedUsers.filter(u => existingPayslipUserIds.has(u.id)).length > 0 && (
                              <p className="text-sm text-primary">
                                  {t('{{count}} already have payroll generated.', {
                                      count: selectedUsers.filter(u => existingPayslipUserIds.has(u.id)).length
                                  })}
                              </p>
                          )}
                          {/* CO-341 — server refusal, kept on screen. Same destructive tone the preflight
                              blockers use (toneClasses.error), so a refusal reads like the rest of the
                              wizard's "you cannot proceed" states rather than a one-off. */}
                          {generateError && (
                              <div
                                  className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3"
                                  role="alert"
                                  data-testid="generate-error"
                              >
                                  <AlertTriangle className="mt-0.5 text-destructive shrink-0" size={20} />
                                  <div className="min-w-0">
                                      <p className="text-sm font-semibold text-destructive">{t('Generation failed')}</p>
                                      <p className="mt-0.5 text-sm text-foreground/80">{generateError}</p>
                                  </div>
                              </div>
                          )}
                      </DialogBody>
                      <DialogFooter>
                          <Button variant="outline" onClick={() => { setGenerateError(null); setStep('preflight'); }}>
                              {t('Back')}
                          </Button>
                          <Button
                              loading={loading}
                              disabled={loading || isFutureGeneratePeriod}
                              onClick={() => generateMonthlyPayroll(selectedUsers)}
                              data-testid="generate-confirm-btn"
                          >
                              {t('Confirm')}
                          </Button>
                      </DialogFooter>
                  </>
              )}
          </DialogContent>
      </Dialog>

      {/* Toast */}
      <ToastRenderer toast={toast} onDismiss={dismissToast} />

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Spinner size="lg" />
        </div>
      )}
    </div>
    </PageLayout>
  );
};

type PreflightTone = 'ok' | 'info' | 'warn' | 'error';

const toneClasses: Record<PreflightTone, { container: string; iconWrap: string; badge: string }> = {
  ok: {
    container: 'border-emerald-500/30 bg-emerald-500/5',
    iconWrap: 'text-emerald-600',
    badge: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  },
  // Neutral "the run will do something you should know about" — tenant primary tint, so it reads as
  // information rather than a problem next to the amber warnings.
  info: {
    container: 'border-primary/25 bg-primary/5',
    iconWrap: 'text-primary',
    badge: 'bg-primary/10 text-primary border-primary/30',
  },
  warn: {
    container: 'border-amber-500/30 bg-amber-500/5',
    iconWrap: 'text-amber-600',
    badge: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  },
  error: {
    container: 'border-destructive/30 bg-destructive/5',
    iconWrap: 'text-destructive',
    badge: 'bg-destructive/10 text-destructive border-destructive/30',
  },
};

interface PreflightSectionProps {
  icon: React.ComponentType<{ className?: string; size?: number }>;
  tone: PreflightTone;
  title: string;
  count: number;
  names: string[];
  footnote?: string;
  testId?: string;
}

const PreflightSection: React.FC<PreflightSectionProps> = ({ icon: Icon, tone, title, count, names, footnote, testId }) => {
  const isOk = tone === 'ok' || count === 0;
  const DisplayIcon = isOk ? CheckCircle2 : Icon;
  const displayTone = isOk ? 'ok' : tone;
  const displayClasses = toneClasses[displayTone];
  return (
    <div className={`rounded-lg border p-3 ${displayClasses.container}`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <DisplayIcon className={`mt-0.5 ${displayClasses.iconWrap}`} size={20} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-sm text-foreground">{title}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${displayClasses.badge}`}>
              {isOk ? '✓' : count}
            </span>
          </div>
          {!isOk && names.length > 0 && (
            <ul className="mt-2 text-xs text-foreground/80 list-disc ps-4 space-y-0.5 max-h-28 overflow-y-auto tabular-nums">
              {names.slice(0, 10).map((n, i) => <li key={i}>{n}</li>)}
              {names.length > 10 && <li className="italic text-muted-foreground">…+{names.length - 10}</li>}
            </ul>
          )}
          {footnote && <p className="mt-2 text-xs text-muted-foreground">{footnote}</p>}
        </div>
      </div>
    </div>
  );
};

export default TablePayroll;
